import { TrainerSearchResult } from '@rover/rsdk/src/apiClient/latest/model/trainerSearchResult';

import { SkillsAndBehaviorSlugsDictionary } from '../utils/skillAndBehaviorQueryParams';

export type GroupedResults = {
  fullMatchResults: TrainerSearchResult[];
  partialMatchResults: TrainerSearchResult[];
  otherResults: TrainerSearchResult[];
};

export const checkBContainsA = (
  arrayA: string[],
  arrayB: string[]
): 'full' | 'partial' | 'none' | 'emptyArray' => {
  if (arrayA.length === 0) {
    return 'emptyArray';
  }

  const setA = new Set(arrayA);
  const setB = new Set(arrayB);

  let matches = 0;

  for (const item of setA) {
    if (setB.has(item)) {
      matches += 1;
    }
  }

  if (matches === arrayA.length) {
    return 'full';
  }

  if (matches === 0) {
    return 'none';
  }

  return 'partial';
};

type GetGroupedResultsParams = {
  selectedSkillsAndBehaviors: SkillsAndBehaviorSlugsDictionary;
  results: TrainerSearchResult[] | undefined;
};

export const getGroupedResults = ({
  selectedSkillsAndBehaviors,
  results,
}: GetGroupedResultsParams): GroupedResults => {
  const groupedResults: GroupedResults = {
    fullMatchResults: [],
    partialMatchResults: [],
    otherResults: [],
  };

  // We are using this to store an ordered version of partial and non-matches
  // for situations in which there are full result matches.
  const partialAndOtherResults: TrainerSearchResult[] = [];

  const { skillsSlugs, behaviorsSlugs } = selectedSkillsAndBehaviors;

  if (!results || (skillsSlugs.length === 0 && behaviorsSlugs.length === 0)) {
    return groupedResults;
  }

  for (const result of results) {
    const skillMatch = checkBContainsA(skillsSlugs, result.skills);
    const behaviorMatch = checkBContainsA(behaviorsSlugs, result.behaviors);
    if (
      ['full', 'emptyArray'].includes(skillMatch) &&
      ['full', 'emptyArray'].includes(behaviorMatch)
    ) {
      groupedResults.fullMatchResults.push(result);
    } else {
      partialAndOtherResults.push(result);
      if (
        ['none', 'emptyArray'].includes(skillMatch) &&
        ['none', 'emptyArray'].includes(behaviorMatch)
      ) {
        groupedResults.otherResults.push(result);
      } else {
        groupedResults.partialMatchResults.push(result);
      }
    }
  }

  if (groupedResults.fullMatchResults.length > 0) {
    groupedResults.partialMatchResults = [];
    groupedResults.otherResults = partialAndOtherResults;
  }

  return groupedResults;
};

/**
 * A util for consistent coordinate parsing for both query parameter
 * strings and form state values.
 */
export const parseCoordinate = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string' && value.length > 0) {
    return Number(value);
  }

  return undefined;
};

/**
 * The inverse of parseCoordinate — used to encode the coordinate state back to
 * a string for URL query parameters, while preserving undefined.
 */
export const encodeCoordinate = (value: number | undefined): string | undefined => {
  if (typeof value === 'number') {
    return String(value);
  }

  return undefined;
};
